<template>
  <!-- The timeline, in two rows. Underneath, one bar per view with its name and how many steps it
       holds — every view, not only the one playing, because a tour of nine views whose names are
       hidden behind a chevron is a tour nobody can see the shape of. Above it, the steps of the view
       that is open, one tick each: that is what a click goes to. Clicking a view's bar opens it,
       which swaps the row of ticks; it does not jump, because its ticks are what jumping is for. -->
  <div class="vhelp__player">
    <v-btn
      :icon="isFinished ? 'mdi-replay' : playing ? 'mdi-pause' : 'mdi-play'"
      variant="text"
      size="small"
      density="comfortable"
      :aria-label="isFinished ? t('help.player.replay') : playing ? t('help.player.pause') : t('help.player.play')"
      aria-keyshortcuts="Space"
      @click="togglePlay"
    />

    <!-- Reading the timeline holds the clock, exactly as reading the dot or the text does: browsing
         the views while the walkthrough plays on had the step change under the reader, and the peek
         they were reading end up beside a step that had already moved. -->
    <div
      ref="timelineRef"
      class="vhelp__timeline"
      @mouseenter="enterTimeline"
      @mouseleave="leaveTimeline"
      @focusin="enterTimeline"
      @focusout="leaveTimeline"
    >
      <div class="vhelp__steps" :class="{ 'is-related': openSection !== null && isRelatedSection(openSection) }">
        <button
          v-for="entry in openEntries"
          :key="entry.index"
          type="button"
          class="vhelp__segment"
          :class="{ 'is-current': entry.index === displayIndex }"
          :aria-label="t('help.player.segment', { number: entry.number, total: playable.length })"
          :aria-current="entry.index === displayIndex ? 'step' : undefined"
          @click="seek(entry.index)"
          @mouseenter="showStepPreview(entry.index, $event)"
          @focus="showStepPreview(entry.index, $event)"
          @blur="preview = null"
        >
          <span class="vhelp__segment-track">
            <span class="vhelp__segment-fill" :style="{ transform: `scaleX(${progressOf(entry.index)})` }" />
          </span>
        </button>
      </div>

      <!-- The related views — a settings page, an employee — carry the theme's second colour, on their
           bars and their names, so a settings page never reads as the seventh tab. Same shapes as
           every other view: only the colour says "something else". -->
      <div class="vhelp__chapters">
        <button
          v-for="group in timeline"
          :key="group.section"
          type="button"
          class="vhelp__chapter"
          :class="{
            'is-open': group.section === openSection,
            'is-current': group.section === currentSection,
            'is-related': isRelatedSection(group.section),
          }"
          :aria-label="
            t(
              'help.player.section',
              { view: sectionHeading(group.section), count: group.entries.length },
              group.entries.length
            )
          "
          :aria-expanded="group.section === openSection ? 'true' : 'false'"
          :style="{ flexGrow: group.entries.length }"
          @click="openSection = group.section"
          @mouseenter="showGroupPreview(group, $event)"
          @focus="showGroupPreview(group, $event)"
          @blur="preview = null"
        >
          <span class="vhelp__chapter-name">
            <span class="vhelp__chapter-label text-truncate">{{ sectionLabel(group.section) }}</span>
            <!-- The count is the one figure that says how much of the tour is behind a name. -->
            <span class="vhelp__chapter-count">{{ group.entries.length }}</span>
          </span>
          <span class="vhelp__segment-track">
            <span class="vhelp__segment-fill" :style="{ transform: `scaleX(${groupProgress(group)})` }" />
          </span>
        </button>
      </div>

      <!-- A peek before going there: the view, and the step's text or how many steps it has. Drawn
           here rather than as a `v-tooltip`, which would open behind the dialog. -->
      <div v-if="preview" class="vhelp__preview" :style="{ left: `${preview.left}px` }">
        <span class="vhelp__preview-view">{{ sectionHeading(preview.section) }}</span>
        {{ preview.text }}
      </div>
    </div>

    <lew-view-help-speed-menu />

    <v-btn
      icon="mdi-keyboard-outline"
      variant="text"
      size="small"
      density="comfortable"
      class="vhelp__shortcuts-toggle"
      :aria-label="t('help.player.shortcuts')"
      :aria-expanded="shortcutsOpen ? 'true' : 'false'"
      aria-keyshortcuts="?"
      @click="emit('toggle-shortcuts')"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * The walkthrough's player bar: play and pause, the timeline grouped by view, speed, and the way to
 * the keyboard shortcuts.
 */
import { computed, ref, watch } from "vue"
import { useViewHelp } from "@/composables/viewHelp/useViewHelp"
import { useViewHelpCopy } from "@/composables/viewHelp/useViewHelpCopy"

const props = defineProps<{
  /** Whether the shortcut list is open — for the keyboard button's state. */
  shortcutsOpen: boolean
}>()

const emit = defineEmits<{ "toggle-shortcuts": [] }>()

/** How far a peek's centre keeps from the timeline's ends: half its width, so it never overhangs. */
const PREVIEW_HALF_WIDTH = 110

const {
  steps,
  playable,
  sections,
  stepsInSection,
  isRelatedSection,
  currentSection,
  displayIndex,
  isFinished,
  landed,
  playing,
  progressOf,
  setHold,
  textOf,
  seek,
  togglePlay,
} = useViewHelp()
const { t, sectionLabel, sectionHeading } = useViewHelpCopy()

const timelineRef = ref<HTMLElement | null>(null)

/** Whether the reader is at the timeline — with the pointer, or with the keyboard. */
const hovering = ref(false)

function enterTimeline() {
  hovering.value = true
  setHold(true)
}

function leaveTimeline() {
  hovering.value = false
  setHold(false)
  preview.value = null
}

/** The view whose steps the timeline shows. Follows playback; a click on another view's bar opens that. */
const openSection = ref<string | null>(null)

const preview = ref<{ left: number; text: string; section: string } | null>(null)

type TimelineGroup = { section: string; entries: { index: number; number: number }[] }

/** The timeline's segments, grouped by section — usually the view each step is on. */
const timeline = computed<TimelineGroup[]>(() =>
  sections.value.map(section => ({
    section,
    entries: stepsInSection(section).map(index => ({ index, number: playable.value.indexOf(index) + 1 })),
  }))
)

/** The steps whose ticks are showing: those of the view that is open. */
const openEntries = computed(() => timeline.value.find(group => group.section === openSection.value)?.entries ?? [])

/** How far through a whole view the reader is, for its bar. */
function groupProgress(group: TimelineGroup) {
  if (!group.entries.length) return 0
  return group.entries.reduce((total, entry) => total + progressOf(entry.index), 0) / group.entries.length
}

/** Where a peek sits: centred over what it is about, kept inside the timeline. */
function previewLeft(event: Event) {
  const target = event.currentTarget as HTMLElement | null
  const bar = timelineRef.value
  if (!target || !bar) return null
  const centre = target.getBoundingClientRect().left - bar.getBoundingClientRect().left + target.offsetWidth / 2
  return Math.min(
    Math.max(centre, PREVIEW_HALF_WIDTH),
    Math.max(PREVIEW_HALF_WIDTH, bar.clientWidth - PREVIEW_HALF_WIDTH)
  )
}

function showGroupPreview(group: TimelineGroup, event: Event) {
  const left = previewLeft(event)
  if (left === null) return
  preview.value = { left, text: t("help.player.steps", group.entries.length), section: group.section }
}

function showStepPreview(index: number, event: Event) {
  const left = previewLeft(event)
  if (left === null) return
  preview.value = {
    left,
    text: textOf(steps.value[index]),
    section: steps.value[index].section ?? steps.value[index].route,
  }
}

// A step coming down releases the hold (see `leaveStep`), which is right when the mouse was on the
// dot — it goes with the step — and wrong here: the timeline stays under the reader's mouse while a
// step they clicked is being moved to, and the walkthrough would play on from under them.
watch(landed, arrived => {
  if (!arrived || !hovering.value) return
  // Only while the reader is still at the timeline. A tick that had keyboard focus is removed when the
  // row of ticks follows playback into another view, and a removed element sends no `focusout`: the
  // hold came back after every landing and the walkthrough never read on.
  const el = timelineRef.value
  if (el && (el.matches(":hover") || el.contains(document.activeElement))) setHold(true)
  else leaveTimeline()
})

// The open section follows the walkthrough into each new view as it sets off for it — `currentSection`
// is the view of the step being moved to, so the row of ticks is the new view's while the pointer is
// still walking to its tab. A view the reader opened by hand
// stays open until playback crosses into another one, which is when they would want it to move.
watch(
  currentSection,
  section => {
    if (section) openSection.value = section
  },
  { immediate: true }
)

// The shortcut list opens over the timeline; a peek left standing under it would be a stray.
watch(
  () => props.shortcutsOpen,
  open => {
    if (open) preview.value = null
  }
)
</script>

<style scoped>
.vhelp__player {
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
  padding: 8px 12px;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 0 0 16px 16px;
}

.vhelp__timeline {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 5px;
  min-width: 0;
}

/*
 * The open view's steps, one tick each, across the whole width. Their own row rather than inside the
 * view's bar: with nine views, a bar wide enough to lay two dozen ticks out legibly left the other
 * eight as stubs, and their names — the thing that says what a tour holds — with no room to be read.
 */
.vhelp__steps {
  display: flex;
  gap: 3px;
  min-width: 0;
}

/*
 * One bar per view, each named, and the longer the view the wider its bar — see `.vhelp__chapter`.
 * Equal widths were the first try and read as arbitrary: they said a two-step view and a
 * twenty-four-step one were the same size, which is the one thing the row is there to answer.
 */
.vhelp__chapters {
  display: flex;
  gap: 8px;
  min-width: 0;
}

.vhelp__chapter {
  display: flex;
  flex-direction: column;
  /*
   * The basis is the name and its count, so **every view is named** whatever its size — a bar that
   * shows a bare number says nothing about the view it stands for. What is left of the row is then
   * handed out by step count (`flex-grow`, set per view in the template), so a long view is wider
   * than a short one without a short one being squeezed out of its own name.
   */
  flex: 0 1 auto;
  gap: 2px;
  min-width: 0;
  padding: 0 0 2px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.vhelp__chapter-name {
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  line-height: 1.2;
  text-transform: uppercase;
  opacity: 0.5;
  transition: opacity 0.2s ease;
}

.vhelp__chapter-label {
  min-width: 0;
}

/* Tabular figures, so a row of counts does not jitter as the bar redraws. */
.vhelp__chapter-count {
  flex: none;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  opacity: 0.75;
}

.vhelp__chapter.is-open .vhelp__chapter-name,
.vhelp__chapter:hover .vhelp__chapter-name {
  opacity: 0.85;
}

/* The view that is playing is named in the primary colour, whether or not it is the open one. */
.vhelp__chapter.is-current .vhelp__chapter-name {
  color: rgb(var(--v-theme-primary));
}

/*
 * Two weights, so the rows read as what they are: the ticks above are the steps, and thicker; the
 * bars below sum a view up, and are thinner. The open view's bar keeps the weight of its ticks, since
 * it is the one they belong to.
 */
.vhelp__steps .vhelp__segment-track {
  height: 6px;
}

.vhelp__steps .vhelp__segment:hover .vhelp__segment-track,
.vhelp__steps .vhelp__segment:focus-visible .vhelp__segment-track,
.vhelp__steps .vhelp__segment.is-current .vhelp__segment-track {
  height: 9px;
}

.vhelp__chapter .vhelp__segment-track {
  height: 3px;
}

.vhelp__chapter.is-open .vhelp__segment-track,
.vhelp__chapter:hover .vhelp__segment-track {
  height: 5px;
}

.vhelp__chapter:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
  border-radius: 4px;
}

/*
 * The related views — a settings page, an employee — carry the theme's second colour, on their bars,
 * their names and the ticks of their steps, so a settings page never reads as the seventh tab. Same
 * shapes as every other view: only the colour says "something else".
 */
.vhelp__chapter.is-related .vhelp__segment-fill,
.vhelp__steps.is-related .vhelp__segment-fill {
  background: rgb(var(--v-theme-accent));
}

.vhelp__chapter.is-related .vhelp__chapter-name {
  color: color-mix(in srgb, rgb(var(--v-theme-accent)) 62%, rgb(var(--v-theme-on-surface)));
  opacity: 0.85;
}

.vhelp__chapter.is-related:focus-visible,
.vhelp__steps.is-related .vhelp__segment:focus-visible {
  outline-color: rgb(var(--v-theme-accent));
}

/* The button is taller than the bar it draws, so it is easy to hit. */
.vhelp__segment {
  display: flex;
  align-items: center;
  flex: 1 1 0;
  min-width: 6px;
  height: 18px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  animation: vhelp-segment-in 0.25s ease both;
}

@keyframes vhelp-segment-in {
  from {
    opacity: 0;
  }
}

.vhelp__segment-track {
  position: relative;
  display: block;
  width: 100%;
  height: 4px;
  overflow: hidden;
  border-radius: 2px;
  background: rgba(var(--v-theme-on-surface), 0.14);
  transition: height 0.15s ease;
}

.vhelp__segment:hover .vhelp__segment-track,
.vhelp__segment:focus-visible .vhelp__segment-track,
.vhelp__segment.is-current .vhelp__segment-track {
  height: 6px;
}

/* A view's bar sums up its steps, so it is a little fainter than the ticks of the open one above it. */
.vhelp__chapter .vhelp__segment-fill {
  opacity: 0.75;
}

.vhelp__segment:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
  border-radius: 2px;
}

/* `scaleX` rather than width, so the per-frame fill stays on the compositor. */
.vhelp__segment-fill {
  position: absolute;
  inset: 0;
  background: rgb(var(--v-theme-primary));
  transform-origin: left center;
}

.vhelp__preview {
  position: absolute;
  bottom: calc(100% + 8px);
  z-index: 1;
  width: 220px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgb(var(--v-theme-on-surface));
  color: rgb(var(--v-theme-surface));
  font-size: 0.75rem;
  line-height: 1.4;
  pointer-events: none;
  transform: translateX(-50%);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
}

.vhelp__preview-view {
  display: block;
  margin-bottom: 2px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.7;
}

/* Narrow: the names go, the bars and their counts stay. A truncated word per view says nothing. */
@media (max-width: 700px) {
  .vhelp__chapters {
    gap: 5px;
  }

  .vhelp__chapter-label {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .vhelp__segment-track,
  .vhelp__chapter-name {
    transition: none;
  }

  .vhelp__segment {
    animation: none;
  }
}
</style>
